import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
  IonText,
  IonProgressBar,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonButton,
  IonAlert,
} from '@ionic/react';
import {
  syncOutline,
  storefrontOutline,
  layersOutline,
  trashOutline,
} from 'ionicons/icons';
import { databaseService } from '../../services/offline/DatabaseService';
import { offlineModeManager } from '../../services/offline/OfflineModeManager';
import { storageCapacityService } from '../../services/offline/StorageCapacityService';

interface OfflineOperationsPanelProps {
  onClearCache: () => Promise<void>;
  hasPendingConflicts: boolean;
}

const OfflineOperationsPanel: React.FC<OfflineOperationsPanelProps> = ({ onClearCache, hasPendingConflicts }) => {
  const [tableStats, setTableStats] = useState<any>({});
  const [storageQuota, setStorageQuota] = useState<any>(null);
  const [showClearAlert, setShowClearAlert] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  useEffect(() => {
    loadInitialData();
    const interval = setInterval(() => {
      loadStorageData();
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const loadInitialData = async () => {
    await loadTableStats();
    await loadStorageData();
    const offlineStatus = offlineModeManager.getCurrentStatus();
    setLastSyncTime(offlineStatus.lastSyncTime);
  };

  const loadTableStats = async () => {
    try {
      const stats = await databaseService.getStorageStats();
      setTableStats(stats.tableStats);
    } catch (error) {
      console.error('Error loading table stats:', error);
    }
  };

  const loadStorageData = async () => {
    try {
      const quota = await storageCapacityService.getStorageQuota();
      setStorageQuota(quota);
    } catch (error) {
      console.error('Error loading storage quota:', error);
    }
  };

  const formatBytes = (bytes: number) => {
    return storageCapacityService.formatStorageSize(bytes);
  };

  const getStorageColor = () => {
    if (!storageQuota) return 'primary';
    if (storageQuota.percentage > 0.9) return 'danger';
    if (storageQuota.percentage > 0.7) return 'warning';
    return 'success';
  };

  const importantTables = [
    'cached_inspections',
    'cached_checklist_items',
    'cached_attachments'
  ];

  const handleClearAll = async (): Promise<void> => {
    await onClearCache();
    await loadTableStats();
    await loadStorageData();
  };

  return (
    <IonCard style={{ margin: '16px 0' }}>
      <IonCardHeader>
        <IonCardTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IonIcon icon={layersOutline} />
          Offline Operations
        </IonCardTitle>
      </IonCardHeader>

      <IonCardContent>
        <IonGrid>
          {/* Storage Summary Row */}
          <IonRow>
            <IonCol size="12" sizeMd="6">
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <IonIcon icon={storefrontOutline} style={{ marginRight: '8px' }} />
                  <IonText>
                    <strong>Storage Usage</strong>
                  </IonText>
                </div>
                {storageQuota && (
                  <>
                    <IonProgressBar value={storageQuota.percentage} color={getStorageColor()} />
                    <IonText color="medium" style={{ fontSize: '0.8rem' }}>
                      <p style={{ margin: '4px 0' }}>
                        {formatBytes(storageQuota.used)} / {formatBytes(storageQuota.total)} 
                        ({Math.round(storageQuota.percentage * 100)}%)
                      </p>
                    </IonText>
                  </>
                )}
              </div>
            </IonCol>

            <IonCol size="12" sizeMd="6">
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <IonIcon icon={syncOutline} style={{ marginRight: '8px' }} />
                  <IonText>
                    <strong>Last Sync</strong>
                  </IonText>
                </div>
                <IonText color="medium" style={{ fontSize: '0.8rem' }}>
                  <p style={{ margin: '4px 0' }}>
                    {lastSyncTime 
                      ? lastSyncTime.toLocaleString()
                      : 'Never synced'
                    }
                  </p>
                </IonText>
              </div>
            </IonCol>
          </IonRow>

          {/* Cache Tables Summary */}
          <IonRow>
            <IonCol size="12">
              <div style={{ marginBottom: '16px' }}>
                <IonText>
                  <h4 style={{ margin: '0 0 12px 0' }}>Cached Data</h4>
                </IonText>
                <IonList style={{ background: 'transparent' }}>
                  {importantTables.map(table => {
                    const stats = tableStats[table];
                    const rowCount = stats?.rowCount || 0;
                    const size = stats?.size || 0;
                    
                    return (
                      <IonItem key={table} style={{ '--padding-start': '0px' }}>
                        <IonLabel>
                          <h3 style={{ fontSize: '0.9rem', margin: 0 }}>
                            {table.replace('cached_', '').replace('_', ' ')}
                          </h3>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                            {formatBytes(size)}
                          </p>
                        </IonLabel>
                        <IonBadge color={rowCount > 0 ? 'success' : 'medium'} slot="end">
                          {rowCount}
                        </IonBadge>
                      </IonItem>
                    );
                  })}
                </IonList>
              </div>
            </IonCol>
          </IonRow>

          {/* Clear Cache Button */}
          <IonRow>
            <IonCol size="12">
              {hasPendingConflicts && (
                <IonText color="warning" style={{ fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                  Resolve all conflicts before clearing cached module data.
                </IonText>
              )}
              <IonButton
                expand="block"
                fill="outline"
                color="danger"
                onClick={() => setShowClearAlert(true)}
                disabled={hasPendingConflicts}
                style={{ marginTop: '8px' }}
              >
                <IonIcon icon={trashOutline} slot="start" />
                Clear All Cached Module Data
              </IonButton>
            </IonCol>
          </IonRow>

        </IonGrid>

        <IonAlert
          isOpen={showClearAlert}
          onDidDismiss={() => setShowClearAlert(false)}
          header="Clear All Cached Module Data"
          message="This will permanently delete all locally cached inspections, checklist items, and attachments for the current module. This cannot be undone."
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Clear All',
              handler: () => {
                handleClearAll();
              },
            },
          ]}
        />
      </IonCardContent>
    </IonCard>
  );
};

export default OfflineOperationsPanel;